import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ReactZoomPanPinchProps,
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { useSetAlertCameraPlayerMouseOverPosition } from 'features/cameras/hooks/useAlertCameraPlayerMouseOverPosition';

type MouseOverContainerProps = {
  children: ReactNode;
};

const clampWithinBounds = (
  number: number,
  lowerBound: number,
  upperBound: number,
): number => {
  return Math.min(Math.max(Number(number), lowerBound), upperBound);
};

const fullSizeStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  cursor: 'crosshair',
};

const transformWrapperProps: ReactZoomPanPinchProps = {
  pinch: { disabled: true },
  doubleClick: { disabled: true },
  wheel: { touchPadDisabled: true, step: 0.1 },
};

export const MouseOverContainer = (
  props: MouseOverContainerProps,
): JSX.Element => {
  const { children } = props;
  const [zoomScale, setZoomScale] = useState(1);
  const setMouseOverPosition = useSetAlertCameraPlayerMouseOverPosition();
  const mouseMoveRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (mouseMoveRef.current) cancelAnimationFrame(mouseMoveRef.current);
      setMouseOverPosition({ mouseX: 0, isHovering: false });
    };
  }, [setMouseOverPosition]);

  const handleZoomStop = useCallback((ref: ReactZoomPanPinchRef): void => {
    setZoomScale(ref.state.scale);
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      const { left, width } = event.currentTarget.getBoundingClientRect();
      const cursorX = (event.clientX - left) / width;

      if (mouseMoveRef.current) cancelAnimationFrame(mouseMoveRef.current);

      if (zoomScale === 1) {
        mouseMoveRef.current = requestAnimationFrame(() => {
          setMouseOverPosition({
            mouseX: clampWithinBounds(cursorX, 0, 1),
            isHovering: true,
          });
        });
        return;
      }

      mouseMoveRef.current = requestAnimationFrame(() => {
        setMouseOverPosition(({ mouseX: prevMouseX }) => {
          const delta = cursorX - prevMouseX;
          const movementFactor = 1 / zoomScale;
          const newX = prevMouseX + delta * movementFactor;
          return { mouseX: clampWithinBounds(newX, 0, 1), isHovering: true };
        });
      });
    },
    [setMouseOverPosition, zoomScale],
  );

  const handleMouseLeave = useCallback(() => {
    if (mouseMoveRef.current) cancelAnimationFrame(mouseMoveRef.current);
    setMouseOverPosition((prevState) => ({ ...prevState, isHovering: false }));
  }, [setMouseOverPosition]);

  return (
    <TransformWrapper {...transformWrapperProps} onZoom={handleZoomStop}>
      <TransformComponent
        wrapperStyle={fullSizeStyle}
        contentStyle={fullSizeStyle}
      >
        <div
          style={fullSizeStyle}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </div>
      </TransformComponent>
    </TransformWrapper>
  );
};
